import { MenuItem } from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DropdownButton } from "shared/components/button/DropdownButton";
import { useUrlParamSync } from "shared/hooks/useUrlParamSync";

export type InternalTemplateEditMode = "view" | "reorder" | "delete";

export const InternalTemplateEditModeDropdown: React.FC<{
  editMode: InternalTemplateEditMode;
  setEditMode: (editMode: InternalTemplateEditMode) => void;
}> = ({ editMode, setEditMode }) => {
  const { t } = useTranslation(["common", "internal"]);

  const editModeToDropdownOption = useMemo<
    Record<InternalTemplateEditMode, string>
  >(
    () => ({
      view: t("common:view"),
      reorder: t("internal:templateTabs.editTemplate.reorderEvents.title"),
      delete: t("internal:templateTabs.editTemplate.deleteEvents.title"),
    }),
    [t],
  );

  const editModes = useMemo(
    () => Object.keys(editModeToDropdownOption) as InternalTemplateEditMode[],
    [editModeToDropdownOption],
  );

  return (
    <DropdownButton
      title={editModeToDropdownOption[editMode]}
      colorScheme="tertiary"
      size="xs"
    >
      {editModes.map((mode) => (
        <MenuItem key={mode} onClick={() => setEditMode(mode)}>
          {editModeToDropdownOption[mode]}
        </MenuItem>
      ))}
    </DropdownButton>
  );
};

export const useTemplateEditModeDropdown = () => {
  const [editMode, setEditMode] = useUrlParamSync({
    paramName: "editMode",
    defaultVal: "view",
  });

  return {
    editMode,
    setEditMode: (mode: InternalTemplateEditMode) => {
      setEditMode(mode);
    },
  };
};
